<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { isWallDisplay, real_id } from 'src/helpers/interface_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	//Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Stores
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import {
		Toggle,
		Alert,
		Button,
		Text,
		Modal,
		Container,
		AdvancedCard,
		AdvancedCardV2,
		IconButton,
		ActionsContainer,
	} from '@Shelly/shelly-design-system';
	//Components
	import NetworkModal from 'src/components/DevicesSettings/NetworkComponents/NetworkModal.svelte';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Device } from 'src/types/Device';
	import { compare } from 'compare-versions';
	import {
		isFwAfter200,
		isGatewayObserver,
		resolveDisplayBleMac,
	} from 'src/helpers/ble_devices_helpers';
	import { onDestroy } from 'svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { MAX_PAIRED_DEVICES, BLE_PAIRING_TIMEOUT_SEC } from 'src/constants';
	import type { BleStatusFlags, BluetoothConfig, PairedDevice } from 'src/types/BleTypes';

	export let device: Device;
	export let isOffline;

	const id = real_id(device.id);
	$: statusStore = getDeviceStatus(id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore;
	const fwVersion = (
		settings?.DeviceInfo?.ver ||
		settings?.sys?.device.fw_id ||
		settings?.DeviceInfo?.fw_id
	)?.match(/(\d{1,}\.){1,}\d{1,}/)?.[0];

	const isAfterOneZero = fwVersion ? compare(fwVersion, '1.0.0', '>=') : '';
	const isAfter200 = isFwAfter200(settings);
	$: isGatewayAlwaysEnabled =
		typeof settings?.ble?.observer?.always_on !== 'undefined' ||
		typeof status?.sys?.btrelay_rev !== 'undefined';

	let enable = settings?.ble?.enable;

	let isObserver = isGatewayObserver(device);
	let observerEnable = isGatewayAlwaysEnabled || settings?.ble?.observer?.enable;
	let rpcEnable = settings?.ble?.rpc?.enable;
	let modal: NetworkModal;

	let pairedDevices: PairedDevice[] = [];
	let loadingPairedDevices = false;
	let bleFlags: BleStatusFlags = [];
	let bleMacFromRpc = '';

	let pairingActive = false;
	let pairingStartedAt = 0;
	let pairingDuration = 0;
	let pairingCountdown = 0;
	let pairingTimer: ReturnType<typeof setInterval> | null = null;
	let statusPollTimer: ReturnType<typeof setInterval> | null = null;

	let showDeleteModal = false;
	let deviceToDelete: PairedDevice | null = null;
	let deletingDevice = false;
	let startingPairing = false;

	$: option = enable ? $format('option_enable') : $format('option_disable');

	// --- Legacy (pre-2.0.0) methods ---
	const setBluetooth = async () => {
		let config: BluetoothConfig = {
			enable,
			observer: isObserver ? { enable: observerEnable } : undefined,
			rpc: isAfterOneZero ? { enable: rpcEnable } : undefined,
		};

		const res: SettingRequest = await gen2Request(device, 'Ble.SetConfig', {
			config,
		});

		if (res?.isok) {
			if (isObserver) {
				settings.ble.observer = { enable: observerEnable };
			}
			settings.ble.enable = enable;
			devices_settings.update_devices_settings(id, settings);
		}
		onSettingsResponse(res, device);
	};

	const toggleEnable = () => {
		enable = !enable;
	};

	const openModal = (e) => {
		enable = e.target.checked;
		modal.open(false);
	};

	const toggleBluetooth = () => {
		setBluetooth();
	};

	const toggleObserver = (e) => {
		observerEnable = e.target.checked;
		setBluetooth();
	};

	const toggleRpc = (e) => {
		rpcEnable = e.target.checked;
		setBluetooth();
	};

	// --- FW 2.0.0+ methods ---

	/**
	 * Normalises the shape returned by `gen2Request` so call sites can
	 * read RPC fields the same way regardless of transport:
	 *  - Cloud path (`gen2CloudReq`) returns `{ isok, data: result }`,
	 *    so the actual fields live under `res.data`.
	 *  - Local path (`localReq`) does `{ ...res.data.result, isok: true }`,
	 *    flattening the result object onto `res` itself.
	 *  - When the result is an array (e.g. `Ble.ListPairedDevices` on
	 *    cloud), `res.data` is the array. On local the spread strips
	 *    `length` and leaves numeric keys, so we recover the array
	 *    from those keys.
	 */
	const pickRpcData = <T = any>(res: any): T => (res?.data ?? res) as T;
	const pickRpcArray = <T>(res: any): T[] => {
		const data = pickRpcData<any>(res);
		if (Array.isArray(data)) return data as T[];
		/**
		 * `Ble.ListPairedDevices` is also seen wrapping the list as
		 * `{ devices: [...] }` on some firmware revisions — accept that
		 * shape too rather than guess at the contract.
		 */
		if (Array.isArray(data?.devices)) return data.devices as T[];
		/** Last resort: localReq spreads array results into numeric keys. */
		if (data && typeof data === 'object') {
			const numericKeys = Object.keys(data).filter((k) => /^\d+$/.test(k));
			if (numericKeys.length > 0) {
				return numericKeys.sort((a, b) => Number(a) - Number(b)).map((k) => data[k]) as T[];
			}
		}
		return [];
	};

	const setRpcEnable180 = async (e) => {
		const newValue = e.target.checked;
		rpcEnable = newValue;

		const config: BluetoothConfig = {
			rpc: { enable: rpcEnable },
		};

		const res: SettingRequest = await gen2Request(device, 'Ble.SetConfig', {
			config,
		});

		if (res?.isok) {
			if (!settings.ble) settings.ble = {};
			if (!settings.ble.rpc) settings.ble.rpc = {};
			settings.ble.rpc.enable = rpcEnable;
			devices_settings.update_devices_settings(id, settings);

			if (rpcEnable) {
				fetchPairedDevices();
			}
		} else {
			rpcEnable = !newValue;
		}
		onSettingsResponse(res, device);
	};

	const fetchPairedDevices = async () => {
		loadingPairedDevices = true;
		try {
			const res: SettingRequest = await gen2Request(device, 'Ble.ListPairedDevices', {});
			if (res?.isok) {
				pairedDevices = pickRpcArray<PairedDevice>(res);
			}
		} catch (e) {
			console.error('Failed to fetch paired devices', e);
		}
		loadingPairedDevices = false;
	};

	const fetchBleStatus = async () => {
		try {
			const res: SettingRequest = await gen2Request(device, 'Ble.GetStatus', {});
			if (res?.isok) {
				const data = pickRpcData<{ mac?: string; flags?: BleStatusFlags; pairing?: any }>(res);
				bleFlags = data?.flags || [];
				bleMacFromRpc = data?.mac ?? '';

				const pairing = data?.pairing;
				if (pairing) {
					if (!pairingActive) {
						pairingActive = true;
						pairingStartedAt = pairing.started_at;
						pairingDuration = pairing.duration;
						startCountdown();
					}
				} else if (pairingActive) {
					stopPairingState();
					fetchPairedDevices();
				}
			}
		} catch (e) {
			console.error('Failed to fetch BLE status', e);
		}
	};

	const startPairing = async () => {
		startingPairing = true;
		try {
			const res: SettingRequest = await gen2Request(device, 'Ble.StartPairing', {
				timeout: BLE_PAIRING_TIMEOUT_SEC,
			});
			if (res?.isok) {
				const timeout = pickRpcData<{ timeout?: number }>(res)?.timeout || 180;
				pairingActive = true;
				pairingStartedAt = Math.floor(Date.now() / 1000);
				pairingDuration = timeout;
				startCountdown();
				startStatusPolling();
			}
			onSettingsResponse(res, device, {}, false);
		} catch (e) {
			console.error('Failed to start pairing', e);
		}
		startingPairing = false;
	};

	const stopPairing = async () => {
		try {
			const res: SettingRequest = await gen2Request(device, 'Ble.StopPairing', {});
			if (res?.isok) {
				stopPairingState();
				// Pick up any device that bonded in the last poll interval.
				fetchPairedDevices();
			}
		} catch (e) {
			console.error('Failed to stop pairing', e);
		}
	};

	const confirmDeleteDevice = (pd: PairedDevice) => {
		deviceToDelete = pd;
		showDeleteModal = true;
	};

	const deleteDevice = async () => {
		if (!deviceToDelete) return;
		deletingDevice = true;
		try {
			const res: SettingRequest = await gen2Request(device, 'Ble.DeletePairedDevice', {
				addr: deviceToDelete.addr,
			});
			if (res?.isok) {
				pairedDevices = pairedDevices.filter((d) => d.addr !== deviceToDelete.addr);
			}
			onSettingsResponse(res, device, {
				success: $format('toast_success_deleted_device', {
					default: 'Successfully deleted device',
				}),
			});
		} catch (e) {
			console.error('Failed to delete paired device', e);
		}
		deletingDevice = false;
		showDeleteModal = false;
		deviceToDelete = null;
	};

	const startCountdown = () => {
		clearCountdown();
		updateCountdown();
		pairingTimer = setInterval(updateCountdown, 1000);
	};

	const updateCountdown = () => {
		const now = Math.floor(Date.now() / 1000);
		const elapsed = now - pairingStartedAt;
		pairingCountdown = Math.max(0, pairingDuration - elapsed);

		if (pairingCountdown <= 0) {
			stopPairingState();
		}
	};

	const stopPairingState = () => {
		pairingActive = false;
		pairingCountdown = 0;
		clearCountdown();
		clearStatusPolling();
	};

	const clearCountdown = () => {
		if (pairingTimer) {
			clearInterval(pairingTimer);
			pairingTimer = null;
		}
	};

	/**
	 * Poll both BLE status and the paired-device list while pairing mode is
	 * active, so newly bonded devices appear without needing a manual refresh
	 * or waiting for the pairing window to close. Calls are awaited
	 * sequentially to avoid stacking parallel RPCs on the same tick.
	 */
	const pollBleWhilePairing = async () => {
		await fetchBleStatus();
		if (pairingActive) {
			await fetchPairedDevices();
		}
	};

	const startStatusPolling = () => {
		clearStatusPolling();
		statusPollTimer = setInterval(pollBleWhilePairing, 3000);
	};

	const clearStatusPolling = () => {
		if (statusPollTimer) {
			clearInterval(statusPollTimer);
			statusPollTimer = null;
		}
	};

	const formatTimestamp = (ts: number): string => {
		if (!ts) return '—';
		return new Date(ts * 1000).toLocaleString();
	};

	const formatCountdown = (seconds: number): string => {
		const m = Math.floor(seconds / 60);
		const s = seconds % 60;
		return `${m}:${s.toString().padStart(2, '0')}`;
	};

	const getBleStatusText = (flags: BleStatusFlags): string => {
		if (!flags || flags.length === 0) return '';
		const details: string[] = [];
		if (flags.includes('scanning'))
			details.push($format('ble_flag_scanning', { default: 'scanning for BLU devices' }));
		if (flags.includes('advertising'))
			details.push($format('ble_flag_advertising', { default: 'advertising' }));
		if (flags.includes('connected'))
			details.push($format('ble_flag_connected', { default: 'device connected' }));
		return details.join(', ');
	};

	$: isAtDeviceLimit = pairedDevices.length >= MAX_PAIRED_DEVICES;
	$: canPair = rpcEnable && !isAtDeviceLimit && !pairingActive && !isOffline;
	$: displayBleMac = resolveDisplayBleMac(device, {
		statusMac: status?.ble?.mac,
		rpcMac: bleMacFromRpc,
	});

	// Init for FW 2.0.0+
	// Calls are awaited sequentially to avoid firing two parallel RPCs in the
	// same tick, which previously caused trid collisions at the socket layer.
	if (isAfter200 && !isOffline) {
		(async () => {
			await fetchBleStatus();
			if (rpcEnable) {
				await fetchPairedDevices();
			}
		})();
	}

	onDestroy(() => {
		clearCountdown();
		clearStatusPolling();
	});
</script>

{#if isAfter200}
	<!-- FW 2.0.0+ Layout -->
	<Text styleType="body-small" class="mb-s">
		{$format('ble_auto_activate_info', {
			default:
				'Bluetooth activates automatically when your device needs it (e.g., for Shelly BLU sensors or automation scripts) and turns off when not in use to save power.',
		})}
	</Text>

	<!-- MAC Address -->
	{#if displayBleMac}
		<Text styleType="body-small" class="mb-s">
			{$format('label_mac_address', { default: 'MAC address' })}:
			<strong>{displayBleMac}</strong>
		</Text>
	{:else if isWallDisplay(device.type)}
		<Alert
			class="mb-s"
			type="info"
			heading={$format('ble_mac_requires_firmware_update', {
				default: 'Bluetooth MAC unavailable',
			})}
			message={$format('ble_mac_requires_firmware_update_message', {
				default:
					'Update your device firmware to the latest version to view the Bluetooth MAC address.',
			})}
		/>
	{/if}

	<!-- Bluetooth Status -->
	<Text styleType="body-small" class="mb-m">
		{$format('sub_settings_title_bluetooth', { default: 'Bluetooth' })}:
		{#if bleFlags.length > 0}
			<strong class="text-success">{$format('ble_status_active', { default: 'Active' })}</strong>
			<span class="text-muted"> — {getBleStatusText(bleFlags)}</span>
		{:else}
			<strong class="text-muted">{$format('ble_status_inactive', { default: 'Inactive' })}</strong>
		{/if}
	</Text>

	<hr class="mt-0 mb-0" />

	<!-- Enable Bluetooth device control toggle -->
	<Toggle
		id="bluetooth-rpc-toggle"
		size="m"
		class="pt-m pb-0"
		checked={rpcEnable}
		on:click={setRpcEnable180}
		disabled={isOffline}
	>
		{$format('ble_enable_device_control', {
			default: 'Enable RPC over Bluetooth',
		})}
	</Toggle>
	<Text styleType="body-small" class="mb-m mt-xs">
		{$format('ble_enable_device_control_helper', {
			default:
				"Allow paired devices to control this Shelly via RPC over Bluetooth. Only devices you've paired can connect.",
		})}
	</Text>

	{#if rpcEnable}
		<hr class="mt-0 mb-m" />

		<!-- Pairing indicator — mirrors the device embedded web:
		     single-line heading with live countdown + Cancel CTA. -->
		{#if pairingActive}
			<Alert
				class="mb-m"
				type="info"
				heading={$format('ble_pairing_active', {
					default: 'Pairing mode active. Time remaining: {countdown}',
					values: { countdown: formatCountdown(pairingCountdown) },
				})}
				cta={{ title: $format('button_cancel', { default: 'Cancel' }) }}
				on:cta_click={stopPairing}
			/>
		{/if}

		<!-- Paired Devices section -->
		<div class="d-flex justify-content-between align-items-center mb-s">
			<Text weight="bold">
				{$format('ble_paired_devices', { default: 'Paired Devices' })}
			</Text>
			<Button size="s" disabled={!canPair || startingPairing} on:click={startPairing}>
				{$format('ble_pair_new_device', { default: 'Pair new device' })}
			</Button>
		</div>

		{#if isAtDeviceLimit}
			<Alert
				class="mb-s"
				type="warning"
				heading={$format('ble_max_paired_warning', {
					default:
						'Maximum of {max} paired devices reached. Remove a device to pair a new one.',
					values: { max: MAX_PAIRED_DEVICES },
				})}
			/>
		{/if}

		{#if loadingPairedDevices}
			<Text color="secondary" class="mb-m">
				{$format('loading', { default: 'Loading...' })}
			</Text>
		{:else if pairedDevices.length === 0}
			<Alert
				type="info"
				heading={$format('ble_no_paired_devices', {
					default: 'No paired devices. Pair a device to control this device via Bluetooth.',
				})}
			/>
		{:else}
			{#each pairedDevices as pd, i (pd.addr)}
				{@const pairedSubtitle = `${$format('ble_paired_at', {
					default: 'Paired',
				})}: ${formatTimestamp(pd.ctime)}`}
				{@const lastConnectedDescription = `${$format('ble_last_connected', {
					default: 'Last connected',
				})}: ${formatTimestamp(pd.atime)}`}
				{#if $showNewLayout}
					<AdvancedCardV2
						class={i < pairedDevices.length - 1 ? 'mb-s' : ''}
						title={pd?.addr?.split(',')?.[0]}
						subtitle={{
							type: 'muted',
							bold: false,
							textSmall: true,
							value: pairedSubtitle,
						}}
						description={{
							type: 'muted',
							bold: false,
							textSmall: true,
							value: lastConnectedDescription,
						}}
						avatar={{ icon: 'fab fa-bluetooth-b' }}
					>
						<ActionsContainer orientation="vertical" type="simple" slot="right">
							<IconButton
								icon="fas fa-trash"
								variant="danger"
								slot="first"
								disabled={isOffline}
								aria-label={capFirstChar(
									$format('button_remove', { default: 'Remove' }),
								)}
								on:click={() => confirmDeleteDevice(pd)}
							/>
						</ActionsContainer>
					</AdvancedCardV2>
				{:else}
					<AdvancedCard
						class={i < pairedDevices.length - 1 ? 'mb-s' : ''}
						title={pd?.addr?.split(',')?.[0]}
						subtitle={{
							type: 'secondary',
							bold: false,
							textSmall: true,
							value: pairedSubtitle,
						}}
						description={{
							type: 'muted',
							bold: false,
							textSmall: true,
							value: lastConnectedDescription,
						}}
						avatar={{ icon: 'fab fa-bluetooth-b' }}
					>
						<div class="d-flex align-items-center" slot="right">
							<IconButton
								slot="second"
								icon="fas fa-trash"
								variant="danger"
								disabled={isOffline}
								aria-label={capFirstChar(
									$format('button_remove', { default: 'Remove' }),
								)}
								on:click={() => confirmDeleteDevice(pd)}
							/>
						</div>
					</AdvancedCard>
				{/if}
			{/each}
		{/if}
	{/if}

	<!-- Delete confirmation modal -->
	<Modal
		on:close={() => {
			showDeleteModal = false;
			deviceToDelete = null;
		}}
		showModal={showDeleteModal}
		backdropClose={true}
		title={$format('label_confirmation', { default: 'Confirmation' })}
	>
		<svelte:fragment slot="content">
			<Container padding="0">
				<Text>
					{$format('ble_confirm_remove_device', {
						default: 'Are you sure you want to remove the paired device {addr}?',
						values: { addr: deviceToDelete?.addr?.split(',')?.[0] || '' },
					})}
				</Text>
			</Container>
		</svelte:fragment>
		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{ sm: 'm' }}
				variant="secondary"
				on:click={() => {
					showDeleteModal = false;
					deviceToDelete = null;
				}}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{ sm: 'm' }}
				disabled={deletingDevice}
				on:click={deleteDevice}
			>
				{$format('button_remove', { default: 'Remove' })}
			</Button>
		</div>
	</Modal>
{:else}
	<!-- Legacy Layout (pre-2.0.0) -->
	<Toggle
		id="bluetooth-enable-toggle"
		size="m"
		class="pb-m"
		checked={enable}
		on:click={openModal}
		disabled={isOffline}
	>
		{capFirstChar($format('sub_settings_title_bluetooth', { default: 'bluetooth' }))}
	</Toggle>
	{#if enable}
		<hr class="mt-0 mb-0" />
		{#if isAfterOneZero}
			<Toggle
				id="bluetooth-rpc-toggle"
				size="m"
				class="pt-m pb-0"
				checked={rpcEnable}
				on:click={toggleRpc}
				disabled={isOffline}
				>{capFirstChar(
					$format('rpc', {
						default: 'RPC',
					}),
				)}
			</Toggle>
		{/if}
		{#if isObserver}
			<Toggle
				id="bluetooth-observer-toggle"
				size="m"
				class={isAfterOneZero ? 'pt-s-plus' : 'pt-m'}
				checked={observerEnable}
				on:click={toggleObserver}
				disabled={isOffline || isGatewayAlwaysEnabled}
				info={isGatewayAlwaysEnabled
					? $format('alert_sub_title_gateway_always_enabled', {
							default:
								'With firmware version 1.5.0, the Bluetooth gateway is consistently enabled',
						})
					: undefined}
				>{capFirstChar(
					$format('title_bluetooth_gateway', {
						default: 'Bluetooth gateway',
					}),
				)}
			</Toggle>
		{/if}
	{/if}
	<NetworkModal
		{isOffline}
		bind:this={modal}
		on:confirm={toggleBluetooth}
		on:close={() => toggleEnable()}
		btnTexts={{
			saveText: enable ? $format('label_enable') : $format('button_disable'),
		}}
		title={enable
			? `${$format('label_enable')} ${$format('sub_settings_title_bluetooth')}?`
			: `${$format('button_disable')} ${$format('sub_settings_title_bluetooth')}?`}
	>
		{#if enable}
			{$format('enable_bluetooth_msg', {
				default:
					'Enabling Bluetooth will allow this device to connect to other Bluetooth devices and use Bluetooth Gateway features.',
			})}
		{:else if option === $format('option_disable')}
			{$format('disable_bluetooth_msg', {
				default:
					'Disabling Bluetooth will prevent this device from connecting to other Bluetooth devices.',
			})}
		{/if}

		<Alert
			class="mt-m"
			type="warning"
			heading={$format('require_device_reboot_prompt', {
				default: 'Note that this action might require a device reboot.',
			})}
		/>
	</NetworkModal>
{/if}
